import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import {
  versandVorschau,
  warmupCapForDay,
  rasterAusFenstern,
  fensterAusRaster,
  rasterVerliertMinuten,
  kommendeFeiertage,
  type SendingPolicy as PolicyRegeln,
  type VersandVorschau,
} from '@mailflow/core/sending';
import { api } from '../lib/api.js';
import { useDebounced } from '../lib/useDebounced.js';
import { Info } from './Info.js';
import { describeWindows, type SendingPolicy, type SendWindow } from '../lib/campaigns.js';

/**
 * Sendeprofil einstellen — und dabei sehen, was die Einstellung bedeutet.
 *
 * Der Bildschirm beantwortet die einzige Frage, die ein Nutzer beim Einstellen
 * wirklich hat: WANN IST MEINE KAMPAGNE FERTIG. Sieben Zahlenfelder allein
 * beantworten sie nicht — deshalb steht die Hochrechnung fest am unteren Rand
 * und aendert sich mit jeder Eingabe.
 *
 * Gerechnet wird mit `versandVorschau` aus @mailflow/core, also mit derselben
 * Funktion, nach der auch der Worker freigibt. Eine zweite, aehnliche Rechnung
 * im Browser wuerde frueher oder spaeter etwas anderes sagen als der Versand
 * tut — und dann verspricht dieser Bildschirm eine Zeit, die niemand haelt.
 */

const WOCHENTAGE = [
  { wert: 1, kurz: 'Mo' },
  { wert: 2, kurz: 'Di' },
  { wert: 3, kurz: 'Mi' },
  { wert: 4, kurz: 'Do' },
  { wert: 5, kurz: 'Fr' },
  { wert: 6, kurz: 'Sa' },
  { wert: 0, kurz: 'So' },
];

interface Vorlage {
  name: string;
  zweck: string;
  werte: Partial<Entwurf>;
}

/**
 * Vier Startpunkte.
 *
 * Wer eine Vorlage nimmt und nichts weiter anfasst, hat eine vernuenftige
 * Einstellung — das ist der Zweck. Die Zahlen sind bewusst vorsichtig: eine zu
 * langsame Kampagne kostet Tage, eine zu schnelle den Absenderruf.
 */
const VORLAGEN: Vorlage[] = [
  {
    name: 'Vorsichtig',
    zweck: 'Kaltes Anschreiben, neue Domain. Beginnt bei 50 am Tag.',
    werte: {
      warmupEnabled: true,
      warmupStart: 50,
      warmupMultiplier: 1.4,
      warmupCap: 2000,
      emailsPerHour: 100,
      emailsPerMinute: 3,
      emailsPerDay: 0,
      fenster: [{ tage: [1, 2, 3, 4, 5], von: '09:00', bis: '17:00' }],
      contactCooldownStunden: 168,
      companyCooldownStunden: 72,
      maxDomainPerHour: 20,
    },
  },
  {
    name: 'Normal',
    zweck: 'Eingespielte Domain, regelmässiger Versand.',
    werte: {
      warmupEnabled: false,
      emailsPerHour: 500,
      emailsPerMinute: 15,
      emailsPerDay: 3000,
      fenster: [{ tage: [1, 2, 3, 4, 5], von: '08:00', bis: '18:00' }],
      contactCooldownStunden: 48,
      companyCooldownStunden: 24,
      maxDomainPerHour: 100,
    },
  },
  {
    name: 'Bestandskunden',
    zweck: 'Empfänger kennen den Absender. Auch samstags.',
    werte: {
      warmupEnabled: false,
      emailsPerHour: 1000,
      emailsPerMinute: 30,
      emailsPerDay: 10000,
      fenster: [
        { tage: [1, 2, 3, 4, 5], von: '07:00', bis: '20:00' },
        { tage: [6], von: '10:00', bis: '16:00' },
      ],
      contactCooldownStunden: 16,
      companyCooldownStunden: 0,
      maxDomainPerHour: 0,
    },
  },
  {
    name: 'Ohne Grenzen',
    zweck: 'Transaktionsmails — Bestätigungen, Zurücksetzen von Passwörtern.',
    werte: {
      warmupEnabled: false,
      emailsPerHour: 0,
      emailsPerMinute: 0,
      emailsPerDay: 0,
      fenster: [{ tage: [0, 1, 2, 3, 4, 5, 6], von: '00:00', bis: '00:00' }],
      contactCooldownStunden: 0,
      companyCooldownStunden: 0,
      maxDomainPerHour: 0,
    },
  },
];

interface Entwurf {
  name: string;
  warmupEnabled: boolean;
  warmupStart: number;
  warmupMultiplier: number;
  warmupCap: number;
  emailsPerHour: number;
  emailsPerMinute: number;
  emailsPerDay: number;
  fenster: SendWindow[];
  blackoutDates: string[];
  contactCooldownStunden: number;
  companyCooldownStunden: number;
  maxDomainPerHour: number;
  timezone: string;
}

function ausProfil(policy: SendingPolicy | null): Entwurf {
  return {
    name: policy?.name ?? 'Standard',
    warmupEnabled: policy?.warmupEnabled ?? true,
    warmupStart: policy?.warmupStart ?? 50,
    warmupMultiplier: policy?.warmupMultiplier ?? 1.4,
    warmupCap: policy?.warmupCap ?? 10_000,
    emailsPerHour: policy?.emailsPerHour ?? 0,
    emailsPerMinute: policy?.emailsPerMinute ?? 0,
    emailsPerDay: policy?.emailsPerDay ?? 0,
    fenster: policy?.sendWindows ?? [{ tage: [1, 2, 3, 4, 5], von: '09:00', bis: '17:00' }],
    blackoutDates: policy?.blackoutDates ?? [],
    // Der Nutzer denkt in Stunden. 960 Minuten liest niemand als "16 Stunden".
    contactCooldownStunden: Math.round((policy?.contactCooldownMinutes ?? 960) / 60),
    companyCooldownStunden: Math.round((policy?.companyCooldownMinutes ?? 960) / 60),
    maxDomainPerHour: policy?.maxDomainPerHour ?? 0,
    timezone: policy?.timezone ?? 'Europe/Zurich',
  };
}

/** Der Entwurf als Regelwerk, wie @mailflow/core es erwartet. */
function alsRegeln(entwurf: Entwurf): PolicyRegeln {
  return {
    emailsPerHour: entwurf.emailsPerHour,
    emailsPerMinute: entwurf.emailsPerMinute,
    emailsPerDay: entwurf.emailsPerDay,
    warmupEnabled: entwurf.warmupEnabled,
    warmupStart: entwurf.warmupStart,
    warmupMultiplier: entwurf.warmupMultiplier,
    warmupCap: entwurf.warmupCap,
    sendWindows: entwurf.fenster,
    blackoutDates: entwurf.blackoutDates,
    contactCooldownMinutes: entwurf.contactCooldownStunden * 60,
    companyCooldownMinutes: entwurf.companyCooldownStunden * 60,
    maxDomainPerHour: entwurf.maxDomainPerHour,
    timezone: entwurf.timezone,
  };
}

const datumFormat = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const GRENZTEXT: Record<VersandVorschau['engsteGrenze'], string> = {
  blackout_date: 'Ausnahmetage',
  outside_send_days: 'Sendetage',
  outside_send_window: 'Sendefenster',
  warmup_cap: 'Warmup-Deckel',
  daily_limit: 'Tageslimit',
  hourly_limit: 'Stundenlimit',
  minute_limit: 'Minutenlimit',
  sender_tageslimit: 'Tageslimit des Absenders',
  keine: 'keine',
};

export interface SendingPolicyEditorProps {
  policy: SendingPolicy | null;
  onClose: () => void;
  /**
   * Empfaengerzahl fuer die Hochrechnung. Aus dem Kampagnen-Editor kommt die
   * echte Zahl; hier ohne Kampagne ist es ein Beispielwert, und die
   * Oberflaeche sagt das auch.
   */
  empfaenger?: number;
  senderTageslimit?: number;
}

export function SendingPolicyEditor({
  policy,
  onClose,
  empfaenger,
  senderTageslimit,
}: SendingPolicyEditorProps) {
  const queryClient = useQueryClient();
  const [entwurf, setEntwurf] = useState<Entwurf>(() => ausProfil(policy));
  const [alsText, setAlsText] = useState(() => rasterVerliertMinuten(entwurf.fenster));
  const [beispielmenge, setBeispielmenge] = useState(String(empfaenger ?? 4000));
  const [neuerAusnahmetag, setNeuerAusnahmetag] = useState('');

  function setze(teil: Partial<Entwurf>) {
    setEntwurf((c) => ({ ...c, ...teil }));
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: entwurf.name.trim(),
        warmupEnabled: entwurf.warmupEnabled,
        warmupStart: entwurf.warmupStart,
        warmupMultiplier: entwurf.warmupMultiplier,
        warmupCap: entwurf.warmupCap,
        emailsPerHour: entwurf.emailsPerHour,
        emailsPerMinute: entwurf.emailsPerMinute,
        emailsPerDay: entwurf.emailsPerDay,
        sendWindows: entwurf.fenster,
        blackoutDates: entwurf.blackoutDates,
        contactCooldownMinutes: entwurf.contactCooldownStunden * 60,
        companyCooldownMinutes: entwurf.companyCooldownStunden * 60,
        maxDomainPerHour: entwurf.maxDomainPerHour,
        timezone: entwurf.timezone.trim(),
      };
      return policy
        ? api.patch<{ policy: SendingPolicy }>(`/api/sending-policies/${policy.id}`, body)
        : api.post<{ policy: SendingPolicy }>('/api/sending-policies', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sending-policies'] });
      onClose();
    },
  });

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label="Sendeprofil">
        <div className="card__body sendeprofil">
          <h2 className="text-lg">{policy ? 'Sendeprofil bearbeiten' : 'Sendeprofil anlegen'}</h2>

          {save.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {save.error instanceof Error ? save.error.message : 'Speichern fehlgeschlagen.'}
            </div>
          )}

          <Field
            label="Name"
            value={entwurf.name}
            onChange={(e) => setze({ name: e.target.value })}
          />

          <Vorlagenwahl
            onWaehlen={(werte) => {
              setEntwurf((c) => ({ ...c, ...werte }));
              setAlsText(false);
            }}
          />

          <Sendeplan
            fenster={entwurf.fenster}
            alsText={alsText}
            onUmschalten={setAlsText}
            onAendern={(fenster) => setze({ fenster })}
          />

          <Mengen entwurf={entwurf} onAendern={setze} />
          <Warmup entwurf={entwurf} onAendern={setze} />
          <Abstaende entwurf={entwurf} onAendern={setze} />

          <Ausnahmetage
            tage={entwurf.blackoutDates}
            neuerTag={neuerAusnahmetag}
            onNeuerTag={setNeuerAusnahmetag}
            onAendern={(blackoutDates) => setze({ blackoutDates })}
          />

          <Field
            label="Zeitzone"
            value={entwurf.timezone}
            onChange={(e) => setze({ timezone: e.target.value })}
            hint="Gilt für Sendefenster, Ausnahmetage und die Tagesgrenze des Tageslimits."
          />

          <Field
            label={empfaenger ? 'Empfänger' : 'Empfänger (Beispiel für die Hochrechnung)'}
            type="number"
            min="1"
            value={beispielmenge}
            onChange={(e) => setBeispielmenge(e.target.value)}
            hint={
              empfaenger
                ? 'Die tatsächliche Empfängerzahl dieser Kampagne.'
                : 'Ohne Kampagne gibt es keine echte Zahl. Trage ein, womit du rechnest.'
            }
          />

          <Hochrechnung
            entwurf={entwurf}
            empfaenger={Number(beispielmenge) || 0}
            senderTageslimit={senderTageslimit}
          />

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={entwurf.name.trim().length === 0}
              loading={save.isPending}
            >
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Vorlagen ---------------------------------------------------------------

function Vorlagenwahl({ onWaehlen }: { onWaehlen: (werte: Partial<Entwurf>) => void }) {
  return (
    <div className="field">
      <span className="field__label">Vorlage übernehmen</span>
      <div className="vorlagen">
        {VORLAGEN.map((vorlage) => (
          <button
            key={vorlage.name}
            type="button"
            className="vorlage"
            onClick={() => onWaehlen(vorlage.werte)}
          >
            <span className="vorlage__name">{vorlage.name}</span>
            <span className="vorlage__zweck">{vorlage.zweck}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Sendeplan: Raster und Textfassung --------------------------------------

function Sendeplan({
  fenster,
  alsText,
  onUmschalten,
  onAendern,
}: {
  fenster: SendWindow[];
  alsText: boolean;
  onUmschalten: (wert: boolean) => void;
  onAendern: (fenster: SendWindow[]) => void;
}) {
  const rundet = rasterVerliertMinuten(fenster);

  return (
    <div className="field">
      <span className="field__label">
        Sendezeiten
        <Info zu="sendefenster" />
      </span>

      {rundet && !alsText && (
        <div className="alert" data-tone="warning" role="status">
          Dieses Profil hat Zeiten mit Minuten (etwa 09:30). Das Raster arbeitet in vollen
          Stunden — sobald du darin etwas änderst, wird gerundet. Genaue Zeiten stellst du
          unter „Als Text bearbeiten“ ein.
        </div>
      )}

      {alsText ? (
        <Fensterliste fenster={fenster} onAendern={onAendern} />
      ) : (
        <Wochenraster fenster={fenster} onAendern={onAendern} />
      )}

      <p className="muted text-sm">{describeWindows(fenster)}</p>

      <div className="dialog__actions">
        <Button variant="ghost" onClick={() => onUmschalten(!alsText)}>
          {alsText ? 'Als Raster bearbeiten' : 'Als Text bearbeiten'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Das Wochenraster.
 *
 * Man malt seine Sendezeiten, statt vier Zahlenfelder auszufuellen. Ziehen
 * setzt oder loescht je nachdem, was die zuerst angefasste Zelle war — wie in
 * einer Tabellenkalkulation.
 *
 * ZUR BEDIENBARKEIT OHNE MAUS: 168 Zellen als Tabstopps waeren unbenutzbar,
 * deshalb tragen sie tabIndex -1. Der gleichwertige Weg ist der Knopf "Als
 * Text bearbeiten" darunter; er fuehrt zu einer Fassung, die sich vollstaendig
 * mit der Tastatur bedienen laesst. Das Raster ist eine Abkuerzung, keine
 * Voraussetzung.
 */
function Wochenraster({
  fenster,
  onAendern,
}: {
  fenster: SendWindow[];
  onAendern: (fenster: SendWindow[]) => void;
}) {
  const raster = useMemo(() => rasterAusFenstern(fenster), [fenster]);
  const [malmodus, setMalmodus] = useState<boolean | null>(null);

  useEffect(() => {
    if (malmodus === null) return;
    const beenden = () => setMalmodus(null);
    window.addEventListener('mouseup', beenden);
    return () => window.removeEventListener('mouseup', beenden);
  }, [malmodus]);

  function setzeZelle(tag: number, stunde: number, an: boolean) {
    const neu = raster.map((zeile, i) =>
      i === tag ? zeile.map((wert, j) => (j === stunde ? an : wert)) : zeile,
    );
    onAendern(fensterAusRaster(neu));
  }

  function toggleTag(tag: number) {
    const allesAn = raster[tag]!.every(Boolean);
    const neu = raster.map((zeile, i) =>
      i === tag ? new Array<boolean>(24).fill(!allesAn) : zeile,
    );
    onAendern(fensterAusRaster(neu));
  }

  function toggleStunde(stunde: number) {
    const allesAn = raster.every((zeile) => zeile[stunde]);
    const neu = raster.map((zeile) => zeile.map((wert, j) => (j === stunde ? !allesAn : wert)));
    onAendern(fensterAusRaster(neu));
  }

  return (
    <div className="raster">
      <div
        className="raster__tabelle"
        role="group"
        aria-label="Sendezeiten je Wochentag und Stunde"
      >
        <span className="raster__ecke" />
        {Array.from({ length: 24 }, (_, stunde) => (
          <button
            key={stunde}
            type="button"
            className="raster__stunde"
            tabIndex={-1}
            title={`${stunde}:00 Uhr an allen Tagen umschalten`}
            onClick={() => toggleStunde(stunde)}
          >
            {stunde % 3 === 0 ? stunde : '·'}
          </button>
        ))}

        {WOCHENTAGE.map((tag) => (
          <Fragment key={tag.wert}>
            <button
              type="button"
              className="raster__tag"
              tabIndex={-1}
              title={`Ganzen ${tag.kurz} umschalten`}
              onClick={() => toggleTag(tag.wert)}
            >
              {tag.kurz}
            </button>
            {Array.from({ length: 24 }, (_, stunde) => {
              const an = raster[tag.wert]![stunde]!;
              return (
                <button
                  key={stunde}
                  type="button"
                  className="raster__zelle"
                  data-an={an}
                  tabIndex={-1}
                  aria-label={`${tag.kurz} ${stunde}:00 ${an ? 'senden' : 'nicht senden'}`}
                  onMouseDown={() => {
                    setMalmodus(!an);
                    setzeZelle(tag.wert, stunde, !an);
                  }}
                  onMouseEnter={() => {
                    if (malmodus !== null) setzeZelle(tag.wert, stunde, malmodus);
                  }}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function Fensterliste({
  fenster,
  onAendern,
}: {
  fenster: SendWindow[];
  onAendern: (fenster: SendWindow[]) => void;
}) {
  function aendere(index: number, teil: Partial<SendWindow>) {
    onAendern(fenster.map((f, i) => (i === index ? { ...f, ...teil } : f)));
  }

  return (
    <div className="stack">
      {fenster.map((f, index) => (
        <article key={index} className="check">
          <div className="tag-row">
            {WOCHENTAGE.map((tag) => (
              <button
                key={tag.wert}
                type="button"
                className="badge"
                data-tone={f.tage.includes(tag.wert) ? 'success' : undefined}
                aria-pressed={f.tage.includes(tag.wert)}
                onClick={() =>
                  aendere(index, {
                    tage: f.tage.includes(tag.wert)
                      ? f.tage.filter((d) => d !== tag.wert)
                      : [...f.tage, tag.wert].sort((a, b) => a - b),
                  })
                }
              >
                {tag.kurz}
              </button>
            ))}
          </div>
          <Field
            label="Von"
            type="time"
            value={f.von}
            onChange={(e) => aendere(index, { von: e.target.value })}
          />
          <Field
            label="Bis"
            type="time"
            value={f.bis}
            onChange={(e) => aendere(index, { bis: e.target.value })}
            hint="Gleiche Zeit für Von und Bis bedeutet ganztags. Liegt „Bis“ vor „Von“, geht das Fenster über Mitternacht."
          />
          <div className="dialog__actions">
            <Button
              variant="ghost"
              onClick={() => onAendern(fenster.filter((_, i) => i !== index))}
            >
              Fenster entfernen
            </Button>
          </div>
        </article>
      ))}
      <Button
        variant="secondary"
        onClick={() => onAendern([...fenster, { tage: [1, 2, 3, 4, 5], von: '09:00', bis: '17:00' }])}
      >
        Fenster hinzufügen
      </Button>
    </div>
  );
}

// --- Mengen, Warmup, Abstände -----------------------------------------------

function Mengen({
  entwurf,
  onAendern,
}: {
  entwurf: Entwurf;
  onAendern: (teil: Partial<Entwurf>) => void;
}) {
  return (
    <>
      <Field
        label="Höchstzahl je Minute"
        type="number"
        min="0"
        value={String(entwurf.emailsPerMinute)}
        onChange={(e) => onAendern({ emailsPerMinute: Number(e.target.value) || 0 })}
        hint="0 = Intelligentes Warmup-Pacing (~1 bis 5 Mails/Min je nach Tag) oder unbegrenzt."
      />
      <Field
        label="Höchstzahl je Stunde"
        type="number"
        min="0"
        value={String(entwurf.emailsPerHour)}
        onChange={(e) => onAendern({ emailsPerHour: Number(e.target.value) || 0 })}
        hint="0 = Intelligentes Warmup-Pacing (~44/Std. Tag 1 bis ~294/Std. Tag 5 über 07:45–11:50 Uhr)."
      />
      <Field
        label="Höchstzahl je Tag"
        type="number"
        min="0"
        value={String(entwurf.emailsPerDay)}
        onChange={(e) => onAendern({ emailsPerDay: Number(e.target.value) || 0 })}
        hint="0 bedeutet unbegrenzt. Gilt zusätzlich zum Warmup — es gewinnt die schärfere Grenze."
      />
    </>
  );
}

/**
 * Warmup mit den ersten sieben Sendetagen als Balken.
 *
 * Ein Multiplikator von 1,4 ist eine abstrakte Zahl; sieben Balken sind es
 * nicht. Wer sieht, dass Tag 1 bei 50 liegt, versteht sofort, warum am ersten
 * Tag nicht 4.000 Mails rausgehen.
 */
function Warmup({
  entwurf,
  onAendern,
}: {
  entwurf: Entwurf;
  onAendern: (teil: Partial<Entwurf>) => void;
}) {
  const tage = useMemo(() => {
    if (!entwurf.warmupEnabled) return [];
    const regeln = alsRegeln(entwurf);
    return Array.from({ length: 7 }, (_, i) => warmupCapForDay(regeln, i + 1));
  }, [entwurf]);

  const hoechster = Math.max(1, ...tage);

  return (
    <>
      <label className="check__head">
        <input
          type="checkbox"
          checked={entwurf.warmupEnabled}
          onChange={(e) => onAendern({ warmupEnabled: e.target.checked })}
        />
        <span>Warmup verwenden</span>
        <Info zu="warmup" />
      </label>

      {entwurf.warmupEnabled && (
        <>
          <Field
            label="Start je Tag"
            type="number"
            min="1"
            value={String(entwurf.warmupStart)}
            onChange={(e) => onAendern({ warmupStart: Number(e.target.value) || 1 })}
            hint="Zulässige Menge am ersten Sendetag."
          />
          <Field
            label="Steigerung je Sendetag"
            type="number"
            min="1"
            step="0.1"
            value={String(entwurf.warmupMultiplier)}
            onChange={(e) => onAendern({ warmupMultiplier: Number(e.target.value) || 1 })}
            hint="Gezählt werden Tage mit tatsächlichem Versand — eine Pause lässt das Limit nicht weiterlaufen."
          />
          <Field
            label="Obergrenze"
            type="number"
            min="1"
            value={String(entwurf.warmupCap)}
            onChange={(e) => onAendern({ warmupCap: Number(e.target.value) || 1 })}
          />

          <div className="field">
            <span className="field__label">Die ersten sieben Sendetage</span>
            <div className="warmup-balken">
              {tage.map((menge, i) => (
                <div key={i} className="warmup-balken__tag">
                  <span className="warmup-balken__wert">{menge.toLocaleString('de-DE')}</span>
                  {/*
                   * Der Anteil ist ein Messwert, kein Gestaltungswert — er
                   * geht deshalb als CSS-Variable hinein. Wie hoch der
                   * Balken daraus wird, entscheidet das Stylesheet.
                   */}
                  <div
                    className="warmup-balken__saeule"
                    style={{ '--anteil': menge / hoechster } as CSSProperties}
                  />
                  <span className="warmup-balken__label">Tag {i + 1}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Abstaende({
  entwurf,
  onAendern,
}: {
  entwurf: Entwurf;
  onAendern: (teil: Partial<Entwurf>) => void;
}) {
  return (
    <>
      <Field
        label="Abstand je Kontakt (Stunden)"
        type="number"
        min="0"
        value={String(entwurf.contactCooldownStunden)}
        onChange={(e) => onAendern({ contactCooldownStunden: Number(e.target.value) || 0 })}
        hint="Mindestabstand zwischen zwei E-Mails an dieselbe Person. 0 bedeutet kein Abstand."
      />
      <Field
        label="Abstand je Firma (Stunden)"
        type="number"
        min="0"
        value={String(entwurf.companyCooldownStunden)}
        onChange={(e) => onAendern({ companyCooldownStunden: Number(e.target.value) || 0 })}
        hint="Verhindert, dass mehrere Personen desselben Betriebs am selben Tag angeschrieben werden."
      />
      <Field
        label="Höchstzahl je Empfängerdomain und Stunde"
        type="number"
        min="0"
        value={String(entwurf.maxDomainPerHour)}
        onChange={(e) => onAendern({ maxDomainPerHour: Number(e.target.value) || 0 })}
        hint="0 bedeutet unbegrenzt. Schützt davor, dass ein einzelner Empfängerserver den Versand als Welle sieht."
      />
    </>
  );
}

// --- Ausnahmetage -----------------------------------------------------------

function Ausnahmetage({
  tage,
  neuerTag,
  onNeuerTag,
  onAendern,
}: {
  tage: string[];
  neuerTag: string;
  onNeuerTag: (wert: string) => void;
  onAendern: (tage: string[]) => void;
}) {
  const feiertage = useMemo(() => kommendeFeiertage(), []);
  const fehlende = feiertage.filter((f) => !tage.includes(f.datum));

  return (
    <div className="field">
      <span className="field__label">Ausnahmetage</span>

      {tage.length === 0 ? (
        <p className="muted text-sm">Keine — es wird an jedem Sendetag versendet.</p>
      ) : (
        <div className="ausnahmetage">
          {[...tage].sort().map((tag) => (
            <span key={tag} className="ausnahmetag">
              {tag}
              <button
                type="button"
                className="ausnahmetag__weg"
                aria-label={`${tag} entfernen`}
                onClick={() => onAendern(tage.filter((t) => t !== tag))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="dialog__actions">
        <Field
          label="Datum hinzufügen"
          type="date"
          value={neuerTag}
          onChange={(e) => onNeuerTag(e.target.value)}
        />
        <Button
          variant="secondary"
          disabled={neuerTag.length === 0 || tage.includes(neuerTag)}
          onClick={() => {
            onAendern([...tage, neuerTag]);
            onNeuerTag('');
          }}
        >
          Hinzufügen
        </Button>
      </div>

      <div className="dialog__actions">
        <Button
          variant="secondary"
          disabled={fehlende.length === 0}
          onClick={() => onAendern([...tage, ...fehlende.map((f) => f.datum)])}
        >
          {fehlende.length === 0
            ? 'Alle Feiertage bereits eingetragen'
            : `Deutsche Feiertage ergänzen (${fehlende.length})`}
        </Button>
      </div>

      <span className="field__hint">
        Bundesweite Feiertage bis Ende des Folgejahres, aus dem Osterdatum berechnet — dazu
        Heiligabend und Silvester, die keine gesetzlichen Feiertage sind, aber als Sendetag
        genauso unbrauchbar. <strong>Feiertage einzelner Bundesländer sind nicht dabei</strong>{' '}
        (Fronleichnam, Allerheiligen, Reformationstag) — sie gelten je nach Land verschieden und
        wären für die Hälfte der Empfänger falsch. Trage sie bei Bedarf von Hand nach.
      </span>
    </div>
  );
}

// --- Die wichtigste Anzeige -------------------------------------------------

/**
 * Was die Einstellung bedeutet — fest am unteren Rand, immer sichtbar.
 *
 * Verzoegert um 400 ms: die Hochrechnung simuliert bis zu 525.600 Minuten,
 * und bei jedem Tastendruck in einem Zahlenfeld waere das verschwendete
 * Arbeit. Dieselbe Ueberlegung wie bei der Segment-Vorschau.
 */
function Hochrechnung({
  entwurf,
  empfaenger,
  senderTageslimit,
}: {
  entwurf: Entwurf;
  empfaenger: number;
  senderTageslimit?: number;
}) {
  const ruhig = useDebounced(entwurf, 400);
  const ruhigeMenge = useDebounced(empfaenger, 400);

  const vorschau = useMemo(
    () =>
      versandVorschau(alsRegeln(ruhig), ruhigeMenge, {
        ...(senderTageslimit ? { senderTageslimit } : {}),
      }),
    [ruhig, ruhigeMenge, senderTageslimit],
  );

  const nieFertig = vorschau.fertig === null;

  return (
    <div className="hochrechnung">
      <div className="hochrechnung__kern" data-nie={nieFertig} role="status">
        {ruhigeMenge <= 0 ? (
          'Keine Empfängerzahl angegeben — ohne sie lässt sich nichts hochrechnen.'
        ) : nieFertig ? (
          'Mit diesen Einstellungen wird diese Kampagne nie fertig.'
        ) : (
          <>
            <strong>{ruhigeMenge.toLocaleString('de-DE')} Empfänger</strong>
            {' · Start '}
            {datumFormat.format(vorschau.start!)}
            {' · fertig voraussichtlich '}
            <strong>gegen {datumFormat.format(vorschau.fertig!)}</strong>
            {vorschau.dauerTage > 1 ? ` (${vorschau.dauerTage} Tage)` : ''}
            {vorschau.engsteGrenze !== 'keine'
              ? ` · engste Grenze: ${GRENZTEXT[vorschau.engsteGrenze]}`
              : ' · keine Grenze greift'}
          </>
        )}
      </div>

      {vorschau.hinweise.map((hinweis) => (
        <p key={hinweis} className="hochrechnung__hinweis">
          {hinweis}
        </p>
      ))}

      {!nieFertig && vorschau.dauerTage > 14 && (
        <p className="hochrechnung__hinweis">
          Der Versand zieht sich über mehr als zwei Wochen. Bei kaltem Anschreiben ist die
          erste Mail dann längst vergessen, wenn die letzte rausgeht.
        </p>
      )}

      {!nieFertig && ruhigeMenge > 0 && (
        <p className="muted text-xs">
          Gerechnet mit derselben Freigabeprüfung, nach der auch der Versand entscheidet. Der
          Worker arbeitet in Stapeln — die tatsächliche Endzeit kann einige Minuten abweichen.
        </p>
      )}
    </div>
  );
}
